import { useEffect, useRef, useState } from 'react'
import { isEqual } from 'date-fns'

import {
  EMPTY_STRING,
  INPUT_AUTOCOMPLETE_VALUE,
} from '../../../constants/common'
import { useVariableRef } from '../../../hooks'
import { isEqualNullable } from '../../../utils/common'
import { InputField } from '../../Input'

function DateTimeInput({
  Component = InputField,
  autoComplete = INPUT_AUTOCOMPLETE_VALUE.off,
  clearable = true,
  format,
  formatHint,
  parse,
  placeholder,
  value = null,
  onBlur,
  onChange,
  onFocus,
  ...componentProps
}) {
  const valueRef = useRef(null)
  const onChangeRef = useVariableRef(onChange)

  const [focused, setFocused] = useState(false)
  const [inputValue, setInputValue] = useState(EMPTY_STRING)

  useEffect(() => {
    const nextValue = parse(inputValue)

    if (!isEqualNullable(nextValue, valueRef.current, isEqual)) {
      valueRef.current = nextValue
      onChangeRef.current?.(nextValue)
    }
  }, [inputValue, onChangeRef, parse])

  useEffect(() => {
    if (value !== valueRef.current) {
      valueRef.current = value
      setInputValue(format(value))
    }
  }, [format, value])

  /** @param {React.UIEvent} event */
  function handleFocus(event) {
    setFocused(true)
    onFocus?.(event)
  }

  /** @param {React.UIEvent} event */
  function handleBlur(event) {
    setFocused(false)
    setInputValue(format(value))
    onBlur?.(event)
  }

  /** @param {React.ChangeEvent} event */
  function handleChange(event) {
    setInputValue(event.target.value)
  }

  function handleClear() {
    setInputValue(EMPTY_STRING)
  }

  return (
    <Component
      {...componentProps}
      autoComplete={autoComplete}
      placeholder={focused ? formatHint : placeholder}
      value={inputValue}
      onBlur={handleBlur}
      onChange={handleChange}
      onClear={clearable ? handleClear : null}
      onFocus={handleFocus}
    />
  )
}

export default DateTimeInput
